import { FIELD_TYPES } from "./field-types.ts";
import type { CustomFieldsById, FieldSlot } from "./spec.ts";

/** The type a field slot resolves to: the referenced custom field's type, or
 *  the slot's own inline override. */
function slotType(
  field: FieldSlot | null | undefined,
  customFieldsById: CustomFieldsById,
): string | null | undefined {
  return field?.customFieldId
    ? customFieldsById[field.customFieldId]?.type
    : field?.overrides?.type;
}

/** The empty value for a field type.
 *
 *  Three distinct empties, and the difference is load-bearing: a collection
 *  type gets `[]`, address gets `{}`, and a type whose control has no natural
 *  empty gets `null`. Everything else gets `""`. */
export function getDefaultValueByType(
  type: string | null | undefined,
): unknown {
  switch (type) {
    case FIELD_TYPES.select:
    case FIELD_TYPES.labels:
    case FIELD_TYPES.member:
    case FIELD_TYPES.photos:
      return [];
    case FIELD_TYPES.address:
      return {};
    case FIELD_TYPES.number:
    // Money is null-when-unset for the same reason as number: 0 is a real
    // amount ($0 deductible) and has to stay distinguishable from "not filled
    // in". Returning "" here sends an empty string down the write path, where
    // Number("") silently coerces it to a stored 0.
    case FIELD_TYPES.money:
    case FIELD_TYPES.date:
    case FIELD_TYPES.dateTime:
    case FIELD_TYPES.time:
    case FIELD_TYPES.phone:
    // Checkbox is tri-state: null = unanswered (renders '—'). A stored
    // false is a deliberate "no" and must not read as empty.
    case FIELD_TYPES.checkbox:
    // A formula's value is derived, never entered: there is nothing to seed
    // a control with, and "" would read as a computed empty string.
    case FIELD_TYPES.formula:
      return null;
    default:
      return "";
  }
}

export function getDefaultValue(
  field: FieldSlot | null | undefined,
  customFieldsById: CustomFieldsById,
): unknown {
  return getDefaultValueByType(slotType(field, customFieldsById));
}
